import { Component, OnInit, ViewChild } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Router, ActivatedRoute } from "@angular/router";
import { VisitReportsApiListQueryParams, VisitReportsService } from "./visitreports.service";
import { BaseMaterialDatatableComponent } from "@impacgroup/angular-baselib";
import { VisitReportListResponseDTO, DateParseMode } from "../../api-models/VisitReport";
import { VisitReportsMainComponent } from "./visitreports.main.component";
import moment from "moment";
import { VisitReportsListStateService } from "./visitreports.liststate.service";

@Component({
    selector: "app-visitreports",
    templateUrl: "./visitreports.component.html",
    styleUrls: ["visitreports.component.scss"],
    standalone: false
})
export class VisitReportsComponent extends BaseMaterialDatatableComponent<VisitReportListResponseDTO> implements OnInit {
    public UTCDATEFORMAT: string = "";

    rows: VisitReportListResponseDTO[] = [];
    totalChecklistCount = 0;

    constructor(
        private visitReportsService: VisitReportsService,
        private translateService: TranslateService,
        private router: Router,
        private route: ActivatedRoute,
        private parent: VisitReportsMainComponent,
        public visitReportsListStateService: VisitReportsListStateService
    ) {
        super({
            stateSaveConfig: {
                stateSaveMode: visitReportsService.datatableStateSaveMode,
                stateSaveComponent: parent,
                stateSaveStorageKey: "dtstate_visitreports_list"
            },
            sortState: {
                active: "dateByMode",
                direction: "desc"
            },
            displayedColumns: ["dateByMode", "user", "checklistCount", "actions"],
            listService: visitReportsService
        });
        this.UTCDATEFORMAT = this.visitReportsService.UTCDATEFORMAT;

        // calculate filter options and set standard filters (datemode === month)
        const endMonth = moment();

        if (!this.visitReportsListStateService.filterStartMonth) {
            this.visitReportsListStateService.filterStartMonth = endMonth.format("YYYY-MM");
        }
        if (!this.visitReportsListStateService.filterEndMonth) {
            this.visitReportsListStateService.filterEndMonth = endMonth.format("YYYY-MM");
        }

        const startMonth = moment().subtract(1, "year");
        this.visitReportsListStateService.monthFilterOptions = [];
        while (startMonth.format("YYYY-MM") !== endMonth.format("YYYY-MM")) {
            this.visitReportsListStateService.monthFilterOptions.push({
                key: endMonth.format("YYYY-MM"),
                value: endMonth.format("MMMM / YYYY")
            });

            endMonth.subtract(1, "month");
        }

        // calculate filter options and set standard filters (datemode === week)
        const endWeek = moment();

        if (!this.visitReportsListStateService.filterStartWeek) {
            this.visitReportsListStateService.filterStartWeek = endWeek.format("YYYY-WW");
        }
        if (!this.visitReportsListStateService.filterEndWeek) {
            this.visitReportsListStateService.filterEndWeek = endWeek.format("YYYY-WW");
        }

        const startWeek = moment().subtract(1, "year");
        this.visitReportsListStateService.weekFilterOptions = [];
        while (startWeek.format("YYYY-WW") !== endWeek.format("YYYY-WW")) {
            this.visitReportsListStateService.weekFilterOptions.push({
                key: endWeek.format("YYYY-WW"),
                value: `KW ${endWeek.format("WW / YYYY")}`
            });

            endWeek.subtract(1, "week");
        }

        // set standard filters (datemode === day)
        if (!this.visitReportsListStateService.filterStartDate) {
            this.visitReportsListStateService.filterStartDate = moment().startOf("month").toDate();
        }
        if (!this.visitReportsListStateService.filterEndDate) {
            this.visitReportsListStateService.filterEndDate = moment().endOf("month").toDate();
        }
    }

    ngOnInit() {
        super.ngOnInit();
        if (!this.visitReportsListStateService.mads) {
            this.subscriptions.push(
                this.visitReportsService.getMADs().subscribe((result) => {
                    this.visitReportsListStateService.mads = result;
                })
            );
        }
    }

    override loadData() {
        this.pageState.isLoading = true;
        // get api query parameters by datatable
        const queryParams: VisitReportsApiListQueryParams = {
            page: this.pageState.currentPage + 1,
            limit: this.pageState.pageSize,
            search: this.pageState.search,
            sort: `${this.pageState.sortState.active}|${this.pageState.sortState.direction}`
        };

        let from: string;
        if (this.visitReportsListStateService.datemode === "day" && this.visitReportsListStateService.filterStartDate) {
            from = moment(this.visitReportsListStateService.filterStartDate).format("YYYY-MM-DD");
        }
        if (this.visitReportsListStateService.datemode === "week" && this.visitReportsListStateService.filterStartWeek) {
            from = this.visitReportsListStateService.filterStartWeek;
        }
        if (this.visitReportsListStateService.datemode === "month" && this.visitReportsListStateService.filterStartMonth) {
            from = this.visitReportsListStateService.filterStartMonth;
        }

        let to: string;
        if (this.visitReportsListStateService.datemode === "day" && this.visitReportsListStateService.filterEndDate) {
            to = moment(this.visitReportsListStateService.filterEndDate).format("YYYY-MM-DD");
        }
        if (this.visitReportsListStateService.datemode === "week" && this.visitReportsListStateService.filterEndWeek) {
            to = this.visitReportsListStateService.filterEndWeek;
        }
        if (this.visitReportsListStateService.datemode === "month" && this.visitReportsListStateService.filterEndMonth) {
            to = this.visitReportsListStateService.filterEndMonth;
        }

        queryParams.mode = this.visitReportsListStateService.datemode;
        queryParams.mads = this.visitReportsListStateService.mads ? this.visitReportsListStateService.mads.filter((mad) => mad.selected === true).map((mad) => mad._id) : undefined;
        queryParams.from = from;
        queryParams.to = to;

        const observable = this.visitReportsService.list(queryParams);

        this.subscriptions.push(
            observable.subscribe({
                next: (data) => {
                    this.dataSource.data = data.list;
                    this.totalChecklistCount = data.totalChecklistCount;

                    setTimeout(() => {
                        if (this.paginator) {
                            this.paginator.pageIndex = this.pageState.currentPage;
                            this.paginator.length = data.count;
                        }
                    });
                    this.pageState.isLoading = false;
                },
                error: (error) => {
                    console.error(error);
                    this.pageState.isLoading = false;
                }
            })
        );
        return observable;
    }

    setDatemode(mode: DateParseMode) {
        this.visitReportsListStateService.datemode = mode;
        this.loadData();
    }

    selectAllMads() {
        this.visitReportsListStateService.mads.forEach((mad) => {
            mad.selected = true;
        });
        this.loadData();
    }

    deselectAllMads() {
        this.visitReportsListStateService.mads.forEach((mad) => {
            mad.selected = false;
        });
        this.loadData();
    }

    dateFilterChanged() {
        this.loadData();
    }

    read(obj: VisitReportListResponseDTO) {
        this.router.navigate(["..", "checklists", "overview"], {
            relativeTo: this.route,
            queryParams: {
                mode: this.visitReportsListStateService.datemode,
                date: obj.dateByMode,
                userFilter: obj.user?._id
            }
        });
    }
}
